import { useState } from 'react'

import { useQuery } from '@apollo/client'
import { Badge, Menu, ActionIcon, Switch } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import dayjs from 'dayjs'

import { useMutation } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import DNButton from 'src/components/Buttons/Button/DNButton'
import DnTable from 'src/components/Core/DnTable'
import AvatarGroup from 'src/components/DataDisplay/AvatarGroup/AvatarGroup'
import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  DISCONNECT_INDEED_CONNECTION,
  JOB_SOURCE_CONNECTIONS_QUERY,
  START_INDEED_CONNECTION,
  UPDATE_INDEED_APPLY_ENABLED,
} from 'src/graphql/jobSourceConnections.graphql'
import IconThreeDots from 'src/icons/IconThreeDots'
import { isAdmin } from 'src/lib/auth/permissions'
import { formatSnakeValue } from 'src/lib/formatters'

import IndeedApplyOptOutModal from './IndeedApplyOptOutModal'
import IndeedSourceNameModal from './IndeedSourceNameModal'

/**
 * Phase 1 Indeed opt-out demo path (ENG-4442):
 * Settings → Integrations → Job Sources → Indeed row ⋯ → Disconnect.
 * Confirm closes live Job Sync postings (including Easily Apply) and stops
 * further sync for that employer account.
 * Per-job alternative: Jobs → Indeed tracking link → Expire on Indeed.
 */

const isIndeedApplyEnabled = (connection: IndeedConnection) =>
  connection.settings?.indeedApplyEnabled !== false

type IndeedConnection = {
  jobSourceConnectionId: string
  externalId: string
  jobSourceName?: string | null
  status: string
  settings?: { indeedApplyEnabled?: boolean } | null
  createdAt: string
  connectedBy?: {
    firstName?: string | null
    lastName?: string | null
  } | null
}

const statusColor = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return 'green'
    case 'INACTIVE':
      return 'gray'
    default:
      return 'gray'
  }
}

const JobSourcesTab = () => {
  const { currentUser } = useAuth()
  // UI convenience only; updateIndeedSourceName is ADMIN-gated server-side.
  const admin = isAdmin(currentUser)
  const [connectionToEditSourceName, setConnectionToEditSourceName] =
    useState<IndeedConnection | null>(null)
  const [
    sourceNameModalOpened,
    { open: openSourceNameModal, close: closeSourceNameModal },
  ] = useDisclosure(false)
  const [connectionToDisconnect, setConnectionToDisconnect] =
    useState<IndeedConnection | null>(null)
  const [
    disconnectModalOpened,
    { open: openDisconnectModal, close: closeDisconnectModal },
  ] = useDisclosure(false)

  const [connectionToOptOut, setConnectionToOptOut] =
    useState<IndeedConnection | null>(null)
  const [
    optOutModalOpened,
    { open: openOptOutModal, close: closeOptOutModal },
  ] = useDisclosure(false)

  const { data, loading } = useQuery(JOB_SOURCE_CONNECTIONS_QUERY, {
    fetchPolicy: 'cache-and-network',
  })

  const [startIndeedConnection, { loading: startingConnection }] = useMutation(
    START_INDEED_CONNECTION,
    {
      onCompleted: (result) => {
        const authorizeUrl = result?.startIndeedConnection?.authorizeUrl
        if (!authorizeUrl) {
          toast('Failed to start Indeed connection', 'error')
          return
        }
        window.location.href = authorizeUrl
      },
      onError: (error) => {
        toast(error.message || 'Failed to start Indeed connection', 'error')
      },
    }
  )

  const [disconnectIndeedConnection] = useMutation(DISCONNECT_INDEED_CONNECTION, {
    onCompleted: () => {
      toast('Indeed account disconnected', 'success')
      closeDisconnectModal()
      setConnectionToDisconnect(null)
    },
    onError: (error) => {
      toast(error.message || 'Failed to disconnect Indeed account', 'error')
    },
    refetchQueries: [JOB_SOURCE_CONNECTIONS_QUERY],
  })

  const [updateIndeedApplyEnabled, { loading: updatingIndeedApply }] =
    useMutation(UPDATE_INDEED_APPLY_ENABLED, {
      onCompleted: (result) => {
        const enabled =
          result?.updateIndeedApplyEnabled?.settings?.indeedApplyEnabled !==
          false
        toast(
          enabled ? 'Indeed Apply turned on' : 'Opted out of Indeed Apply',
          'success'
        )
        closeOptOutModal()
        setConnectionToOptOut(null)
      },
      onError: (error) => {
        toast(error.message || 'Failed to update Indeed Apply', 'error')
      },
      refetchQueries: [JOB_SOURCE_CONNECTIONS_QUERY],
    })

  const connections: IndeedConnection[] = data?.jobSourceConnections?.items ?? []
  const activeConnections = connections.filter(
    (connection) => connection.status === 'ACTIVE'
  )

  const handleConnect = () => {
    startIndeedConnection()
  }

  const handleDisconnect = (connection: IndeedConnection) => {
    setConnectionToDisconnect(connection)
    openDisconnectModal()
  }

  const handleEditSourceName = (connection: IndeedConnection) => {
    setConnectionToEditSourceName(connection)
    openSourceNameModal()
  }

  const handleIndeedApplyToggle = (
    connection: IndeedConnection,
    enabled: boolean
  ) => {
    if (!enabled) {
      setConnectionToOptOut(connection)
      openOptOutModal()
      return
    }

    updateIndeedApplyEnabled({
      variables: {
        jobSourceConnectionId: connection.jobSourceConnectionId,
        indeedApplyEnabled: true,
      },
    })
  }

  const confirmIndeedApplyOptOut = () => {
    if (!connectionToOptOut) return

    updateIndeedApplyEnabled({
      variables: {
        jobSourceConnectionId: connectionToOptOut.jobSourceConnectionId,
        indeedApplyEnabled: false,
      },
    })
  }

  const confirmDisconnect = async () => {
    if (!connectionToDisconnect) return

    await disconnectIndeedConnection({
      variables: {
        jobSourceConnectionId: connectionToDisconnect.jobSourceConnectionId,
      },
    })
  }

  const columns = [
    {
      key: 'employer',
      label: 'Indeed employer',
      width: 24,
      render: (connection: IndeedConnection) => (
        <div className="flex flex-col">
          <div className="text-sm font-medium text-doubleNickel-gray-900">
            {connection.jobSourceName || 'Indeed employer'}
          </div>
          <div className="text-xs text-doubleNickel-gray-500">
            ID: {connection.externalId}
          </div>
        </div>
      ),
    },
    ...(admin
      ? [
          {
            key: 'sourceName',
            label: 'Source name',
            width: 16,
            render: (connection: IndeedConnection) =>
              connection.jobSourceName ? (
                <div className="text-sm text-doubleNickel-gray-700">
                  {connection.jobSourceName}
                </div>
              ) : (
                <Badge variant="light" color="red" size="sm">
                  Not set
                </Badge>
              ),
          },
        ]
      : []),
    {
      key: 'connectedBy',
      label: 'Connected by',
      width: 14,
      render: (connection: IndeedConnection) => (
        <AvatarGroup
          avatars={[
            {
              firstName: connection.connectedBy?.firstName,
              lastName: connection.connectedBy?.lastName,
            },
          ]}
        />
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: 12,
      render: (connection: IndeedConnection) => (
        <Badge variant="light" color={statusColor(connection.status)} size="sm">
          {formatSnakeValue(connection.status)}
        </Badge>
      ),
    },
    {
      key: 'indeedApply',
      label: 'Indeed Apply',
      width: 12,
      render: (connection: IndeedConnection) => (
        <Switch
          aria-label="Indeed Apply"
          checked={isIndeedApplyEnabled(connection)}
          onChange={(event) =>
            handleIndeedApplyToggle(connection, event.currentTarget.checked)
          }
          disabled={connection.status !== 'ACTIVE' || updatingIndeedApply}
          size="sm"
        />
      ),
    },
    {
      key: 'connectedAt',
      label: 'Connected',
      width: 14,
      render: (connection: IndeedConnection) => (
        <div className="text-sm text-doubleNickel-gray-700">
          {dayjs(connection.createdAt).format('MMM D, YYYY')}
        </div>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: 8,
      render: (connection: IndeedConnection) => (
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon variant="subtle" color="gray" size={24}>
              <IconThreeDots className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item onClick={handleConnect}>Reconnect</Menu.Item>
            {admin && (
              <Menu.Item onClick={() => handleEditSourceName(connection)}>
                Edit source name
              </Menu.Item>
            )}
            <Menu.Item
              color="red"
              onClick={() => handleDisconnect(connection)}
              disabled={connection.status !== 'ACTIVE'}
            >
              Disconnect / opt out
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      ),
    },
  ]

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto py-2">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="text-lg font-semibold text-doubleNickel-gray-900">
            Job Sources
          </div>
          <div className="text-sm text-doubleNickel-gray-500">
            Connect third-party job sources and manage linked employer accounts.
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-doubleNickel-gray-200 bg-white p-5">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <div className="text-base font-medium text-doubleNickel-gray-900">
              Indeed
            </div>
            <div className="text-sm text-doubleNickel-gray-500">
              Connect an Indeed employer account to post jobs via Job
              Sync and receive Indeed Apply applicants in Double Nickel.
            </div>
          </div>
          {activeConnections.length === 0 && (
            <DNButton
              variant="primary"
              onClick={handleConnect}
              loading={startingConnection}
            >
              Connect Indeed
            </DNButton>
          )}
        </div>

        <DnTable
          columns={columns}
          data={connections}
          loading={loading}
          emptyStateMessage="No Indeed accounts connected yet."
          enablePagination={false}
        />
      </div>

      {admin && (
        <IndeedSourceNameModal
          connection={connectionToEditSourceName}
          opened={sourceNameModalOpened}
          onClose={closeSourceNameModal}
        />
      )}

      <IndeedApplyOptOutModal
        employerName={connectionToOptOut?.jobSourceName}
        opened={optOutModalOpened}
        loading={updatingIndeedApply}
        onClose={closeOptOutModal}
        onConfirm={confirmIndeedApplyOptOut}
      />

      <DeleteModal
        opened={disconnectModalOpened}
        close={closeDisconnectModal}
        onDelete={confirmDisconnect}
        title="Opt out of Indeed Job Sync"
        description={
          connectionToDisconnect
            ? `Disconnect ${connectionToDisconnect.jobSourceName || 'this Indeed employer'}? This opts out of Indeed Job Sync and Indeed Apply for this account. Live Indeed postings will be expired. Double Nickel jobs stay open.`
            : 'Disconnect this Indeed account? Live Indeed postings will be expired and Job Sync / Indeed Apply will stop.'
        }
      />
    </div>
  )
}

export default JobSourcesTab
